// Iter 136 — TV broadcaster country preference picker.
// Stored on the logged-in user as `tv_country` (ISO code), used by the
// /api/matches/:id/broadcasts endpoint and the "Where to Watch" panel
// inside MatchAnalysisModal to highlight channels available where the
// user lives.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Tv, Globe2, Check } from "lucide-react";
import { Card } from "../components/ui/card";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const TVCountryPicker = ({ inline = false }) => {
  const { user, refreshMe } = useAuth();
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/tv/countries`).then((r) => {
      if (!cancelled) setCountries(r.data?.countries || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const current = countries.find((c) => c.code === user?.tv_country);

  const handlePick = async (code) => {
    if (!user) {
      toast.error("Please log in to save your country preference.");
      return;
    }
    if (code === user.tv_country) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/me/tv-country`, { country_code: code });
      await refreshMe?.();
      const picked = countries.find((c) => c.code === code);
      toast.success(`TV country set to ${picked?.flag || ""} ${picked?.name || code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save TV country");
    } finally {
      setSaving(false);
    }
  };

  const trigger = (
    <DropdownMenuTrigger
      data-testid="tv-country-picker-trigger"
      disabled={saving}
      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3.5 h-9 text-xs font-bold uppercase tracking-[0.14em] transition disabled:opacity-50"
    >
      <Tv className="w-3.5 h-3.5 text-cyan-400" />
      {current ? (
        <>
          <span>{current.flag}</span>
          <span>{current.code}</span>
        </>
      ) : (
        <>
          <Globe2 className="w-3.5 h-3.5" />
          <span>Set country</span>
        </>
      )}
    </DropdownMenuTrigger>
  );

  const menu = (
    <DropdownMenuContent
      align="end"
      className="bg-[#12161d] border-white/[0.08] min-w-[260px] max-h-[60vh] overflow-y-auto"
      data-testid="tv-country-picker-menu"
    >
      <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
        TV broadcast country
      </DropdownMenuLabel>
      <DropdownMenuSeparator className="bg-white/[0.06]" />
      {countries.map((c) => (
        <DropdownMenuItem
          key={c.code}
          data-testid={`tv-country-option-${c.code}`}
          onSelect={() => handlePick(c.code)}
          className="cursor-pointer flex items-center justify-between gap-3 py-2 focus:bg-white/[0.06]"
        >
          <div className="flex items-center gap-2.5">
            <span className="text-base leading-none">{c.flag}</span>
            <div>
              <div className="text-sm font-bold leading-tight">{c.name}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{c.code}</div>
            </div>
          </div>
          {c.code === user?.tv_country && <Check className="w-3.5 h-3.5 text-emerald-400" />}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  );

  if (inline) {
    return (
      <DropdownMenu>
        {trigger}
        {menu}
      </DropdownMenu>
    );
  }

  // Card variant for embedding inside the Account → Profile tab.
  return (
    <Card
      className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.06] via-card to-card p-4 flex items-center gap-3"
      data-testid="tv-country-card"
    >
      <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
        <Tv className="w-5 h-5 text-cyan-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">TV broadcaster country</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {current
            ? `Showing channels available in ${current.flag} ${current.name} first when you open a match.`
            : "Pick your country to highlight channels available where you live."}
        </div>
      </div>
      <DropdownMenu>
        {trigger}
        {menu}
      </DropdownMenu>
    </Card>
  );
};

export default TVCountryPicker;
